import { useState } from 'react'
import React from 'react'
import Head from 'next/head'
import { useRouter } from 'next/router'

import {
  HeaderBar,
  HeaderBarLeftAction,
  HeaderBarRightAction,
  HeaderBarTitle,
} from '@wartek-id/header-bar'
import { historyUtils } from '@wartek-id/toolbox-helper'
import { Icon } from '@wartek-id/icon'
import { Spinner } from '@wartek-id/spinner'

import { SocialShareDrawer } from 'components/SocialShareDrawer'
import { useAPIInfoTerkiniDetail } from 'api/info-terkini/useAPIInfoTerkiniDetail'
import { useSETrackEvent } from 'app/InfoTerkiniDetailPage/hooks/useSETrackEvent'
import { useAuth } from 'utils/auth'
import Detail from '../components/Detail'

export interface InfoTerkiniDetailProps {
  id: string
  isRuangGtkEnabled: boolean
}

const InfoTerkiniDetailApp = (props: InfoTerkiniDetailProps) => {
  const router = useRouter()
  const [shareDrawerOpen, setShareDrawerOpen] = useState(false)
  const {
    SETrackOpenInformationDetails,
    SETrackShareInformation,
    SETrackShareMediaClick,
  } = useSETrackEvent()

  const { session } = useAuth()
  const isLoggedIn = !!session?.user

  const { data, isLoading, isFetched } = useAPIInfoTerkiniDetail(props.id)

  const title = data?.data?.title ?? ''
  const hasSurvey = !!data?.data?.surveyLink
  const [showSurveySheet, setShowSurveySheet] = useState(false)

  const onShare = () => {
    setShareDrawerOpen(true)

    // track share info terkini
    SETrackShareInformation({
      event_trigger: window.location.href,
      announcement_id: data?.data?.id,
    })
  }

  const onShareButtonClicked = (funnel: string) => {
    // track share media click
    SETrackShareMediaClick({
      event_trigger: window.location.href,
      announcement_id: data?.data?.id,
      funnel,
    })
  }

  const onBackButtonPressed = () => {
    if (isLoggedIn && hasSurvey) {
      setShowSurveySheet(true)
    } else {
      historyUtils?.backToApp?.(router?.back)
    }
  }

  React.useEffect(() => {
    if (data) {
      SETrackOpenInformationDetails({
        page_name: window.location.href,
        image_link: data.data.thumbnailUrl,
        announcement_id: data.data.id,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  // NOTE:
  // Syntax for bolding text is different between WhatsApp and Telegram.
  // WhatsApp uses asterisk (*) while Telegram uses double asterisk (**).
  // We use Whatsapp format because it is the most used platform.
  const shareText = `*Info Terkini dari Kemdikbudristek*: ${title}`

  return (
    <>
      <Head>
        <title>Info Terkini - {title}</title>
        <meta name="description" content={`Info Terkini - ${title}`} />
      </Head>
      <HeaderBar>
        <HeaderBarLeftAction onClick={onBackButtonPressed} />

        <HeaderBarTitle>
          {props.isRuangGtkEnabled ? 'Pemberitahuan' : 'Info Terkini'}
        </HeaderBarTitle>

        <HeaderBarRightAction>
          <button
            type="button"
            className="flex gap-x-6"
            data-testid="share-info"
            onClick={onShare}
          >
            <Icon>share</Icon>
          </button>
        </HeaderBarRightAction>
      </HeaderBar>

      {isLoading && <Spinner data-testid="loading-spinner" />}

      {isFetched && (
        <Detail
          data={data.data}
          showSurveySheet={showSurveySheet}
          onCloseSurveySheet={() => setShowSurveySheet(false)}
          onExitButtonPressed={() => historyUtils?.backToApp?.(router?.back)}
        />
      )}

      <SocialShareDrawer
        shareText={shareText}
        isOpen={shareDrawerOpen}
        onClose={() => setShareDrawerOpen(false)}
        shareUrl={window.location.href}
        title="Bagikan Info Terkini"
        onShareButtonClicked={onShareButtonClicked}
      />
    </>
  )
}

export default InfoTerkiniDetailApp
